import { Injectable } from '@angular/core';
import {GroovePlanApiService} from "../grooveplan-api.service";
import {HttpClient} from "@angular/common/http";
import {StorageService} from "../internals/storage.service";
import {NavigationEnd, Router} from "@angular/router";
import {BehaviorSubject, catchError, filter, from, Observable, of, switchMap, take} from "rxjs";
import {UserService} from "./user.service";
import { SecureStorageService } from '../internals/secure-storage.service';
import { NotificationService } from './notification.service';

interface AuthToken {
  valeur: string;
  date_expiration: string;
}

interface RefreshToken {
  valeur: string;
  date_expiration: string;
}

export interface PublicUser {
  nom: string;
  prenom: string;
  regime: string;
  regime_id: number;
  createdAt: string;
  updatedAt: string;
}

export interface PrivateUser {
  id: number;
  email: string;
  telephone: string;
  admin: boolean;
}

export type ComptePartage = PublicUser & PrivateUser;

@Injectable({
  providedIn: 'root'
})
export class AuthService extends GroovePlanApiService {
  private readonly _comptePartage$ = new BehaviorSubject<ComptePartage | null>(null);
  public readonly comptePartage$ = this._comptePartage$.asObservable();
  
  constructor(_httpClient: HttpClient,
              storageService: StorageService,
              secureStorage: SecureStorageService,
              router: Router,
              private userService: UserService,
              private notificationService: NotificationService
            ) {
    super(_httpClient, storageService, secureStorage, router);
    this.initComptePartage()
  }

  private async initComptePartage(): Promise<void> {
    try {
      const [publicData, privateData] = await Promise.all([
        this.storageService.get<PublicUser>('comptePartage'),
        this.secureStorage.get<PrivateUser>('comptePartage'),
      ]);

      if (!publicData && !privateData) {
        this._comptePartage$.next(null);
        return;
      }

      this._comptePartage$.next({
        ...(publicData ?? {}),
        ...(privateData ?? {}),
      } as ComptePartage);

    } catch (error) {
      console.error('[ComptePartage] init failed', error);
      this._comptePartage$.next(null);
    }
  }

  async getGoogleAuthTokensIdsFromState(state: string) {
    let headers = await this.getAuthHeaders();
    return await this.get('auth/google/tokenIds?state='+state, headers);
  }

  async setComptePartage(
    user: ComptePartage | null
  ): Promise<void> {

    if (!user) {
      await Promise.all([
        this.storageService.set('comptePartage', null),
        this.secureStorage.set('comptePartage', null),
      ]);
      this._comptePartage$.next(null);
      return;
    }

    const publicData: PublicUser = {
      nom: user.nom,
      prenom: user.prenom,
      regime: user.regime,
      regime_id: user.regime_id,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
    };

    const privateData: PrivateUser = {
      id: user.id,
      email: user.email,
      telephone: user.telephone,
      admin: user.admin
    };

    await Promise.all([
      this.storageService.set('comptePartage', publicData),
      this.secureStorage.set('comptePartage', privateData),
    ]);

    this._comptePartage$.next(user);
  }



  login(id: string, pwd: string) {
    return this.post('login', {
      identifiant: id,
      password: pwd
    })
  }

  register(formValues: any) {
    return this.post('user/register', {
      nom: formValues.nom,
      prenom: formValues.prenom,
      email: formValues.email,
      password: formValues.motDePasse,
      telephone: formValues.telephone,
    })
  }

  checkToken(token: string): Observable<any> {
    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.get('check-token', headers)
      )
    );
  }

  checkUserLogged(): Observable<boolean> {
    return from(this.secureStorage.get<string>('authToken')).pipe(
      switchMap(token => {
        if (!token) {
          return of(false);
        }
        return this.checkToken(token);
      }),
      catchError(() => of(false))
    );
  }

  async logout() {
    
    let headers = await this.getAuthHeaders()

    return this.post('logout', {}, headers);
  }

  refreshAuthToken(): Observable<any> {
    return from(this.getRefreshHeaders()).pipe(
      switchMap(headers =>
        this.post('refresh-token', {}, headers)
      )
    );
  }

  /**
   * Traitement commun après authentification
   * (email/password, Google, refresh, deeplink)
   */
  async handleAuthSuccess(resAuth: any): Promise<void> {
    if (!resAuth?.authToken) {
      throw new Error(resAuth?.message || 'Authentification échouée');
    }

    const authToken = resAuth.authToken;
    const refreshToken = resAuth.refreshToken;

    // 1️⃣ Sauvegarde tokens
    await this.saveTokens(authToken, refreshToken);

    // 2️⃣ Récupération utilisateur
    const req = await this.userService.getOneById(authToken.user_id);

    return new Promise((resolve, reject) => {
      req.pipe(take(1)).subscribe({
        next: async (user: any) => {
          if (!user) {
            return reject('Utilisateur introuvable');
          }

          // 3️⃣ Store user
          this.userService.storeUserInStorage(user);

          // 4️⃣ Notifications
          await this.notificationService.initialiserNotifications();

          if(this.router.url !== '/plan-de-salle') {
            // 5️⃣ Navigation finale
            await this.router.navigate(['/']);
          }
          
          resolve();
        },
        error: err => reject(err)
      });
    });
  }

  async saveTokens(
    authToken: any,
    refreshToken?: any
  ): Promise<void> {
    await this.secureStorage.set('authToken', authToken.valeur);
    await this.secureStorage.set('expAuthToken', authToken.date_expiration);

    if (refreshToken) {
      await Promise.all([
        this.secureStorage.set('refreshToken', refreshToken.valeur),
        this.secureStorage.set('expRefreshToken', refreshToken.date_expiration),
      ]);
    }
  }


  async takeControl(user: any) {
    const initialUser = await this.userService.getUserInStorage('user');
    this.userService.storeUserInStorage(initialUser, 'initialUser');
    this.userService.storeUserInStorage(user);
    await this.setComptePartage(user)
  }

  async releaseControl() {
    const initialUser = await this.userService.getUserInStorage('initialUser');
    this.userService.storeUserInStorage(initialUser, 'user');
    this.userService.storeUserInStorage(null, 'initialUser');
    await this.setComptePartage(null)
  }
}
